import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, MessageCircle, ArrowRight, Sparkles, Globe, Headphones, Zap, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";
import { collection, addDoc, getFirestore } from "firebase/firestore";
import { firebaseApp } from "../firebase";
import BookDemoModal from "../components/BookDemoModal";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const db = getFirestore(firebaseApp);
      const contactSubmission = {
        ...formData,
        submittedAt: new Date(),
        timestamp: new Date().toISOString(),
      };

      await addDoc(collection(db, "contactSubmissions"), contactSubmission);

      toast.success("Message sent successfully! We'll get back to you soon.");

      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: Phone,
      title: "Phone Support",
      description: "Speak directly with our team",
      value: "+91 8019430022",
      link: "tel:+918019430022",
      availability: "Mon-Fri, 9 AM - 6 PM IST"
    },
    {
      icon: Mail,
      title: "Email Support",
      description: "Send us a detailed message",
      value: "info@vetriaai.com",
      link: "mailto:info@vetriaai.com",
      availability: "Response within 2 hours"
    },
    {
      icon: MessageCircle,
      title: "WhatsApp",
      description: "Quick chat support",
      value: "Chat with us",
      link: "https://wa.me/918019430022",
      availability: "Available 24/7",
      external: true
    },
    {
      icon: Globe,
      title: "Website",
      description: "Visit our platform",
      value: "www.vetriaai.com",
      link: "https://www.vetriaai.com",
      availability: "Always available",
      external: true
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: "Quick Response",
      description: "We typically respond within 2 hours during business hours"
    },
    {
      icon: Headphones,
      title: "Expert Support",
      description: "Get help from our experienced team members"
    },
    {
      icon: CheckCircle,
      title: "24/7 Availability",
      description: "Reach us anytime via WhatsApp or email"
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Modern Navigation - Simplified */}
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-0 -ml-1 group">
            <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8 sm:h-9 sm:w-9" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent group-hover:from-red-800 group-hover:via-primary group-hover:to-red-800 transition-all duration-300">
              VetriaAI
            </span>
          </Link>
          <Link
            to="/login"
            className="bg-primary text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-sm sm:text-base shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            Sign In
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.1),transparent_50%)]"></div>

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-gray-200 mb-6 sm:mb-8">
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
            <span className="text-xs sm:text-sm font-semibold text-gray-700">Get in Touch</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
            Get in Touch
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 px-4 max-w-3xl mx-auto">
            We'd love to hear from you—whether you're an HR leader looking to transform your hiring process,
            a candidate seeking opportunities, or an investor interested in our vision.
          </p>
        </div>
      </section>

      {/* Why Contact Us Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-gray-50 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Why Contact Us</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              We're Here to Help
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4 max-w-3xl mx-auto">
              Our team is committed to providing exceptional support and answering all your questions
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20 hover:-translate-y-2 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/5 rounded-card flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Contact Us</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Choose Your Preferred Method
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              Multiple ways to reach us - pick what works best for you
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            {/* Contact Methods */}
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8">Contact Information</h3>

              <div className="space-y-4 sm:space-y-6">
                {contactMethods.map((method, index) => {
                  const Icon = method.icon;
                  return (
                    <a
                      key={index}
                      href={method.link}
                      target={method.external ? "_blank" : undefined}
                      rel={method.external ? "noopener noreferrer" : undefined}
                      className="flex items-start space-x-4 p-4 sm:p-5 bg-white rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20 group"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/5 rounded-card flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base group-hover:text-primary transition-colors">
                          {method.title}
                        </h4>
                        <p className="text-gray-600 hover:text-primary transition-colors text-sm sm:text-base mb-1">
                          {method.value}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500">{method.availability}</p>
                      </div>
                    </a>
                  );
                })}
              </div>

              {/* Location Card */}
              <div className="mt-6 sm:mt-8 p-4 sm:p-5 bg-gradient-to-br from-primary/5 to-primary/10 rounded-card border border-primary/20">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-card flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Location</h4>
                    <p className="text-gray-600 text-sm sm:text-base">
                      India (Expanding globally in 2026)
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                      H. No. 5-95/1, Flat No. G, Mokkapati Towers, Fathenagar Colony,
                      K.v.rangareddy, Balanagar, Telangana, India, 500018
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-6 sm:p-8 rounded-card shadow-card border border-gray-100">
              <div className="mb-6">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">Send us a Message</h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
              </div>

              <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-primary">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-button focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-primary">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-button focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject <span className="text-primary">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-button focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                    placeholder="What's this about?"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message <span className="text-primary">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-button focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-sm sm:text-base resize-none"
                    placeholder="Tell us more about your inquiry..."
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-white py-3 px-6 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-red-800 to-red-900"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

        <div className="relative max-w-4xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 px-2">
            Ready to Transform Your Hiring?
          </h2>
          <p className="text-base sm:text-lg md:text-xl opacity-95 mb-8 px-4">
            Book Your Demo and see how VetriaAI can revolutionize your recruitment process
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="group bg-white text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-gray-50 transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Book Your Demo
            <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="md:col-span-1">
              <Link to="/" className="mb-4 inline-flex items-center gap-0 -ml-1">
                <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8" />
                <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-400 to-primary bg-clip-text text-transparent">
                  VetriaAI
                </span>
              </Link>
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                Revolutionizing recruitment with intelligent automation.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Product</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/features" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Company</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Support</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/help-center" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Help Center
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/cancellations-refunds" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Cancellations &amp; Refunds
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Digital Delivery
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="text-sm sm:text-base">&copy; 2025 VetriaAI. All copyrights are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>

      <BookDemoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default Contact;
